import { c as _c } from "react/compiler-runtime";
import { useObservable } from '@legendapp/state/react';
import { useEffect, useState } from 'react';
import { type Observable, batch } from '@legendapp/state';
import type { Easing, PanInfo, TargetAndTransition } from 'framer-motion';
import type { GradientCardProps } from '@/components/cards/GradientCard';
import debounce from '@/lib/debounce';
import { positiveModulo } from '@/lib/math';
const VELOCITY_THRESHOLD = 600;
const cardPositions = [{
  rotate: 0,
  zIndex: 3
}, {
  rotate: -3,
  zIndex: 2
}, {
  rotate: -6,
  zIndex: 1
}] as const;
const CardPositionIndex = {
  Top: 0,
  Middle: 1,
  Bottom: 2
} as const;
export type CardState = {
  index: number;
  itemIndex: number;
  positionIndex: number;
  draggable?: boolean;
  gradientShade: GradientCardProps['shade'];
  status?: 'current' | 'animating' | 'idle' | 'last' | 'dragging';
  animation: TargetAndTransition & {
    'z-index'?: number | number[];
  };
};
const cards: CardState[] = [{
  index: 0,
  positionIndex: CardPositionIndex.Top,
  itemIndex: 0,
  gradientShade: 'light',
  status: 'current',
  draggable: true,
  animation: {
    ...cardPositions[CardPositionIndex.Top]
  }
}, {
  index: 1,
  positionIndex: CardPositionIndex.Middle,
  itemIndex: 1,
  gradientShade: 'mid',
  status: 'idle',
  draggable: true,
  animation: {
    ...cardPositions[CardPositionIndex.Middle]
  }
}, {
  index: 2,
  positionIndex: CardPositionIndex.Bottom,
  itemIndex: 2,
  gradientShade: 'dark',
  status: 'idle',
  draggable: true,
  animation: {
    ...cardPositions[CardPositionIndex.Bottom]
  }
}] as const;
const PageDirection = {
  Next: 1,
  Previous: -1
} as const;
export type PaginationDirections = (typeof PageDirection)[keyof typeof PageDirection];
const cardUtils = {
  total: cards.length,
  mod: (n: number) => positiveModulo(n, cardUtils.total),
  getDelay: (positionIndex: number, referenceIndex: number, direction: PaginationDirections): number => 0.1 * (direction === 1 ? cardUtils.mod(positionIndex - referenceIndex) : cardUtils.mod(referenceIndex - positionIndex)),
  getPositionIndex: (cardIndex: number, page: number): number => cardUtils.mod(cardIndex - page)
};
export type RefType = <ViewportElement extends HTMLElement>(instance: ViewportElement | null) => void;
export type PageAnimationOptions = {
  animationDirection?: PaginationDirections;
  ease?: Easing;
  velocity?: number;
};
export function useAnimatedCardStackState(items) {
  const $ = _c(17);
  const [keyCaptureContainer, setKeyCaptureContainer] = useState();
  const [viewport, setViewport] = useState();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = structuredClone(cards);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== items) {
    t1 = card$ => items[card$.itemIndex.get() % items.length];
    $[1] = items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const cardState$ = useObservable({
    cards: t0,
    itemCount: items.length,
    activeCardIndex: 0,
    activeItemIndex: 0,
    cardWidth: 700,
    animationDirection: PageDirection.Next as PaginationDirections,
    getItemForCard: t1,
    getActiveItem: () => items[cardState$.activeItemIndex.get()],
    startCardPaginateAnimation: (animatingCard$, t2) => {
      const {
        animationDirection: t3,
        ease: t4,
        velocity
      } = t2;
      const animationDirection = t3 === undefined ? 1 : t3;
      const ease = t4 === undefined ? "anticipate" : t4;
      const x = animationDirection * cardState$.cardWidth.get();
      animatingCard$.status.set("animating");
      animatingCard$.animation.x.set(x);
      animatingCard$.animation.rotate.set(animationDirection === PageDirection.Next ? cardPositions[CardPositionIndex.Bottom].rotate : cardPositions[CardPositionIndex.Top].rotate);
      animatingCard$.animation.transition.set({
        ease,
        velocity
      });
    },
    paginate: (pageDirection, t5) => {
      const {
        animationDirection: animationDirection_0,
        ...animationOptions
      } = t5 === undefined ? {} : t5;
      batch(() => {
        const currentItemIndex = cardState$.activeItemIndex.get();
        const currentCardIndex = cardState$.activeCardIndex.get();
        const nextCardIndex = cardUtils.mod(currentCardIndex + pageDirection);
        const nextItemIndex = positiveModulo(currentItemIndex + pageDirection, items.length);
        if (pageDirection === PageDirection.Previous) {
          const backCard = cardState$.cards.find(_temp);
          if (backCard) {
            backCard.itemIndex.set(nextItemIndex);
          }
        }
        const animatingIndex = pageDirection === PageDirection.Next ? currentCardIndex : cardUtils.mod(currentCardIndex - 1);
        const animatingCard$_0 = cardState$.cards[animatingIndex];
        if (!animatingCard$_0) {
          return;
        }
        cardState$.startCardPaginateAnimation(animatingCard$_0, {
          animationDirection: animationDirection_0 || pageDirection,
          ...animationOptions
        });
        cardState$.cards.forEach(card$_0 => {
          card$_0.positionIndex.set(cardUtils.getPositionIndex(card$_0.index.get(), nextCardIndex));
        });
        cardState$.activeItemIndex.set(nextItemIndex);
        cardState$.activeCardIndex.set(nextCardIndex);
        cardState$.animationDirection.set(pageDirection);
      });
    },
    jumpTo: targetIndex => {
      batch(() => {
        const currentIndex = cardState$.activeItemIndex.get();
        if (targetIndex === currentIndex) {
          return;
        }
        const total = cardState$.itemCount.get();
        const direction = targetIndex > currentIndex && targetIndex - currentIndex <= total / 2 || currentIndex - targetIndex > total / 2 ? PageDirection.Next : PageDirection.Previous;
        const steps = direction === PageDirection.Next ? positiveModulo(targetIndex - currentIndex, total) : positiveModulo(currentIndex - targetIndex, total);
        for (let i = 0; i < steps; i++) {
          setTimeout(() => {
            cardState$.paginate(direction);
          }, i * 150);
        }
      });
    },
    handleAnimationComplete: card$_1 => {
      if (card$_1.status.peek() !== "animating") {
        return;
      }
      const direction_0 = cardState$.animationDirection.get();
      batch(() => {
        const positionIndex = card$_1.positionIndex.get();
        if (direction_0 === PageDirection.Next && positionIndex === CardPositionIndex.Bottom) {
          const middleCard = cardState$.cards.find(_temp2);
          if (middleCard) {
            const newIndex = positiveModulo(middleCard.itemIndex.peek() + 1, items.length);
            card$_1.itemIndex.set(newIndex);
          }
        }
        card$_1.animation.set({
          ...cardPositions[card$_1.positionIndex.get()],
          x: 0
        });
        card$_1.status.set("idle");
        cardState$.cards.forEach(card => {
          if (card.status.get() === "animating" || card.index.get() === card$_1.index.get()) {
            return;
          }
          const currentPositionIndex = card.positionIndex.get();
          const isCurrentCard = card.index.get() === card$_1.index.get();
          card.animation.set({
            ...cardPositions[currentPositionIndex],
            x: 0,
            transition: {
              delay: cardUtils.getDelay(card.positionIndex.get(), positionIndex, direction_0)
            }
          });
          card.draggable.set(isCurrentCard);
          if (isCurrentCard) {
            card.status.set("current");
          }
        });
      });
    },
    handleDragStart: _temp3,
    handleDrag: (card$_3, info) => {
      const cardWidth = cardState$.cardWidth.get();
      const minRotate = cardPositions[CardPositionIndex.Top].rotate;
      const maxRotate = cardPositions[CardPositionIndex.Bottom].rotate;
      const x_0 = Math.max(-cardWidth, Math.min(cardWidth, Math.abs(info.offset.x)));
      const rotation = minRotate + (maxRotate - minRotate) * x_0 / cardWidth;
      card$_3.animation.rotate.set(rotation);
    },
    handleDragEnd: (card$_4, info_0) => {
      card$_4.status.set("current");
      const offset = info_0.offset.x;
      const velocity_0 = info_0.velocity.x;
      const positionIndex_0 = card$_4.positionIndex.get();
      if (Math.abs(offset) > cardState$.cardWidth.get() / 2 || Math.abs(velocity_0) > VELOCITY_THRESHOLD) {
        cardState$.paginate(PageDirection.Next, {
          animationDirection: offset > 0 ? PageDirection.Next : PageDirection.Previous,
          ease: "linear",
          velocity: velocity_0 * 2
        });
      } else {
        const stateX = card$_4.animation.x.get();
        const x_1 = typeof stateX === "number" ? stateX : 0;
        batch(() => {
          card$_4.animation.rotate.set(cardPositions[positionIndex_0].rotate);
          card$_4.animation.x.set(x_1 > 0 ? -0.1 : 0.1);
        });
      }
    },
    handlePrevClicked: () => {
      cardState$.paginate(PageDirection.Previous);
    },
    handleNextClicked: () => {
      cardState$.paginate(PageDirection.Next, {});
    }
  });
  let t6;
  if ($[3] !== cardState$ || $[4] !== keyCaptureContainer) {
    t6 = () => {
      if (!keyCaptureContainer) {
        return;
      }
      const handleKeyDown = function handleKeyDown(e) {
        if (e.key === "ArrowLeft") {
          cardState$.paginate(PageDirection.Previous);
        } else {
          if (e.key === "ArrowRight") {
            cardState$.paginate(PageDirection.Next);
          }
        }
      };
      keyCaptureContainer.addEventListener("keydown", handleKeyDown);
      return () => keyCaptureContainer.removeEventListener("keydown", handleKeyDown);
    };
    $[3] = cardState$;
    $[4] = keyCaptureContainer;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== cardState$ || $[7] !== keyCaptureContainer) {
    t7 = [cardState$, keyCaptureContainer];
    $[6] = cardState$;
    $[7] = keyCaptureContainer;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] !== cardState$ || $[10] !== viewport) {
    t8 = () => {
      if (!viewport) {
        return;
      }
      const handleResize = debounce(() => {
        if (!viewport?.offsetWidth) {
          return;
        }
        cardState$.cardWidth.set(viewport.offsetWidth);
      });
      handleResize();
      const resizeObserver = new ResizeObserver(() => handleResize());
      resizeObserver.observe(viewport);
      return () => {
        resizeObserver?.disconnect();
      };
    };
    $[9] = cardState$;
    $[10] = viewport;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== cardState$ || $[13] !== viewport) {
    t9 = [cardState$, viewport];
    $[12] = cardState$;
    $[13] = viewport;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  if ($[15] !== cardState$) {
    t10 = [cardState$, setKeyCaptureContainer as RefType, setViewport as RefType];
    $[15] = cardState$;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10 as const;
}
function _temp3(card$_2) {
  card$_2.status.set("dragging");
  card$_2.animation.transition.delete();
}
function _temp2(c_0) {
  return c_0.positionIndex.peek() === CardPositionIndex.Middle;
}
function _temp(c) {
  return c.positionIndex.peek() === CardPositionIndex.Bottom;
}
export type AnimatedCardStackState<ItemType> = ReturnType<typeof useAnimatedCardStackState<ItemType>>[0];